import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Eye, EyeOff, Pencil, ShieldCheck, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { subjectsWizard, type SubjectPhiResponse, type SubjectWizardState } from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { normalisePhone } from '@/lib/phone';
import { errorText } from '@/lib/permissionError';

/**
 * Subject Profile - shows the full identity/contact/address/highlights bundle
 * for a subject. Follows the PHI-hidden-standard-view rule:
 *
 *  - Values are MASKED by default even for roles that have plaintext access
 *    (the BE only sends plaintext when the role is authorized, but we still
 *    require an explicit "Reveal" click before displaying it).
 *  - Fields the role cannot see (BE returned a redacted mask like
 *    "[redacted]" or "1998-XX-XX") never unmask; the header shows a
 *    "hidden by policy" note next to them.
 *  - Edit is grouped by section (Identity, Contact, Address, Highlights).
 *    Contact still routes through /contact-info because that endpoint
 *    format-validates E.164 phone + email and drives the Remote-Sign gate.
 *    Everything else routes through /profile.
 */

const IDENTITY_KEYS = ['full_name', 'preferred_name', 'date_of_birth'] as const;
const CONTACT_KEYS = ['email_personal', 'phone_e164'] as const;
const ADDRESS_KEYS = [
  'address_street',
  'address_city',
  'address_state',
  'address_zip',
  'address_country',
] as const;
const HIGHLIGHTS_KEYS = ['highlights'] as const;
const REGULATORY_KEYS = ['mrn', 'ssn_last4'] as const;
// Sex/gender come from the onboarding wizard's demographics step
// (`ctms_subjects.demographics_sex/gender`), not the PHI bundle — they
// aren't a Safe Harbor identifier the backend redacts, so they render
// unmasked here just like they do during onboarding.
const DEMOGRAPHICS_KEYS = ['demographics_sex', 'demographics_gender'] as const;
// Study identifiers. NOT PHI and NOT blinding-sensitive: an IVRF reference
// locates the subject's record in the IVRS/IWRS, it does not reveal their
// arm. So these render unmasked, like demographics, and are not part of the
// Reveal toggle — there is nothing to hide. The randomization number and
// treatment arm are deliberately NOT here; both are restricted to
// allocation-aware staff and never reach this payload.
const STUDY_ID_KEYS = ['ivrf_number'] as const;

const SEX_OPTIONS = ['Male', 'Female', 'Intersex', 'Unknown', 'Declined to answer'];
const GENDER_OPTIONS = [
  'Man',
  'Woman',
  'Non-binary',
  'Transgender man',
  'Transgender woman',
  'Prefer not to say',
  'Other',
];

const LABELS: Record<string, string> = {
  full_name: 'Legal name',
  preferred_name: 'Preferred name',
  date_of_birth: 'Date of birth',
  email_personal: 'Email',
  phone_e164: 'Phone',
  address_street: 'Street',
  address_city: 'City',
  address_state: 'State / region',
  address_zip: 'Postal code',
  address_country: 'Country',
  highlights: 'Highlights',
  mrn: 'MRN',
  ssn_last4: 'SSN (last 4)',
  demographics_sex: 'Sex (biological)',
  demographics_gender: 'Gender identity',
  ivrf_number: 'Subject ID',
};

const EDITOR_ROLES = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'coordinator',
  'principal_investigator',
  'sub_investigator',
] as const;

function maskFor(value: string | undefined): string {
  if (value === undefined || value === '') return '—';
  return '••••••••';
}

function isEmpty(value: string | undefined): boolean {
  return value === undefined || value === '';
}

interface Props {
  subjectId: string;
  phi: SubjectPhiResponse | null;
  wizardState?: Pick<
    SubjectWizardState,
    'demographics_sex' | 'demographics_gender' | 'ivrf_number'
  > | null;
}

export function SubjectProfileSection({ subjectId, phi, wizardState }: Props) {
  const canEdit = useHasRole([...EDITOR_ROLES]);
  // Collapsed by default — this section's full identity/contact/address/
  // demographics bundle takes up a lot of vertical space that's rarely
  // needed at a glance, so keep the page scannable until someone asks for it.
  const [expanded, setExpanded] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [editing, setEditing] = useState<
    null | 'identity' | 'contact' | 'address' | 'highlights' | 'demographics' | 'studyIds'
  >(null);

  const fields = phi?.fields ?? {};
  const redactedSet = useMemo(() => new Set(phi?.redactedFields ?? []), [phi?.redactedFields]);
  const demographicsValues: Record<string, string | undefined> = {
    demographics_sex: wizardState?.demographics_sex ?? undefined,
    demographics_gender: wizardState?.demographics_gender ?? undefined,
  };
  const studyIdValues: Record<string, string | undefined> = {
    ivrf_number: wizardState?.ivrf_number ?? undefined,
  };

  const renderValue = (key: string): { text: string; masked: boolean; policyHidden: boolean } => {
    const raw = fields[key];
    if (redactedSet.has(key)) {
      // BE already returned the Safe Harbor mask - the role has no
      // plaintext access. Do not reveal, ever.
      return {
        text: raw && raw.length > 0 ? raw : 'Hidden by policy',
        masked: true,
        policyHidden: true,
      };
    }
    if (isEmpty(raw)) return { text: '—', masked: false, policyHidden: false };
    return revealed
      ? { text: raw as string, masked: false, policyHidden: false }
      : { text: maskFor(raw), masked: true, policyHidden: false };
  };

  const renderDemographicsValue = (
    key: string,
  ): { text: string; masked: boolean; policyHidden: boolean } => {
    const raw = demographicsValues[key];
    return { text: isEmpty(raw) ? '—' : (raw as string), masked: false, policyHidden: false };
  };

  // Unmasked, and outside the Reveal toggle: see STUDY_ID_KEYS.
  const renderStudyIdValue = (
    key: string,
  ): { text: string; masked: boolean; policyHidden: boolean } => {
    const raw = studyIdValues[key];
    return { text: isEmpty(raw) ? '—' : (raw as string), masked: false, policyHidden: false };
  };

  return (
    <section className="card p-4">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        className="flex w-full flex-wrap items-center gap-2 text-left cursor-pointer"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
        )}
        <ShieldCheck className="h-4 w-4 text-accent-600" />
        <h2 className="text-sm font-semibold text-ink-primary">Subject Profile</h2>
        <span className="text-xs text-ink-muted">
          {expanded ? 'Sensitive information is masked by default.' : 'Click to view identity, contact, and address details.'}
        </span>
        {expanded && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setRevealed((r) => !r);
            }}
            className="ml-auto inline-flex items-center gap-1 rounded border border-surface-border px-2.5 py-1 text-xs font-semibold text-ink-primary hover:bg-surface-hover"
            aria-pressed={revealed}
          >
            {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {revealed ? 'Hide' : 'Reveal'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-4">
          <FieldGroup
            title="Study identifiers"
            keys={[...STUDY_ID_KEYS]}
            renderValue={renderStudyIdValue}
            onEdit={canEdit ? () => setEditing('studyIds') : undefined}
          />
        </div>
      )}

      {/* Deliberately OUTSIDE the `phi` guard above: the IVRF number lives on
          ctms_subjects, so a subject with no encrypted bundle still has one to
          show and edit. The message below is scoped to the PHI bundle only. */}
      {expanded && !phi && (
        <p className="mt-4 text-sm text-ink-muted">
          No identity, contact or address information on file for this subject.
        </p>
      )}

      {expanded && phi && (
        <div className="mt-4 space-y-5">
          <FieldGroup
            title="Identity"
            keys={[...IDENTITY_KEYS]}
            renderValue={renderValue}
            onEdit={canEdit ? () => setEditing('identity') : undefined}
          />
          <FieldGroup
            title="Demographics"
            keys={[...DEMOGRAPHICS_KEYS]}
            renderValue={renderDemographicsValue}
            onEdit={canEdit ? () => setEditing('demographics') : undefined}
          />
          <FieldGroup
            title="Contact"
            keys={[...CONTACT_KEYS]}
            renderValue={renderValue}
            onEdit={canEdit ? () => setEditing('contact') : undefined}
          />
          <FieldGroup
            title="Address"
            keys={[...ADDRESS_KEYS]}
            renderValue={renderValue}
            onEdit={canEdit ? () => setEditing('address') : undefined}
          />
          <FieldGroup
            title="Highlights"
            keys={[...HIGHLIGHTS_KEYS]}
            renderValue={renderValue}
            onEdit={canEdit ? () => setEditing('highlights') : undefined}
          />
          {REGULATORY_KEYS.some((k) => k in fields) && (
            <FieldGroup
              title="Regulatory IDs"
              keys={[...REGULATORY_KEYS]}
              renderValue={renderValue}
              onEdit={undefined}
            />
          )}
        </div>
      )}

      {editing && (
        <ProfileEditModal
          section={editing}
          subjectId={subjectId}
          currentValues={
            editing === 'demographics'
              ? demographicsValues
              : editing === 'studyIds'
                ? studyIdValues
                : fields
          }
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function FieldGroup({
  title,
  keys,
  renderValue,
  onEdit,
}: {
  title: string;
  keys: string[];
  renderValue: (key: string) => { text: string; masked: boolean; policyHidden: boolean };
  onEdit?: () => void;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{title}</h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-accent-700 hover:text-accent-800"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </div>
      <dl className="grid gap-3 md:grid-cols-2">
        {keys.map((key) => {
          const { text, masked, policyHidden } = renderValue(key);
          return (
            <div key={key} className="border-b border-surface-border pb-2">
              <dt className="flex items-center gap-1 text-xs uppercase tracking-wide text-ink-muted">
                {masked ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {LABELS[key] ?? key}
                {policyHidden && (
                  <span className="ml-1 rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium text-ink-muted">
                    policy
                  </span>
                )}
              </dt>
              <dd
                className={`text-sm ${
                  masked ? 'text-ink-muted italic' : 'text-ink-primary'
                } ${title === 'Highlights' ? 'whitespace-pre-wrap' : ''}`}
              >
                {text}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------------------

const SECTION_FIELDS: Record<
  'identity' | 'contact' | 'address' | 'highlights' | 'demographics' | 'studyIds',
  string[]
> = {
  identity: [...IDENTITY_KEYS],
  contact: [...CONTACT_KEYS],
  address: [...ADDRESS_KEYS],
  highlights: [...HIGHLIGHTS_KEYS],
  demographics: [...DEMOGRAPHICS_KEYS],
  studyIds: [...STUDY_ID_KEYS],
};

function ProfileEditModal({
  section,
  subjectId,
  currentValues,
  onClose,
}: {
  section: 'identity' | 'contact' | 'address' | 'highlights' | 'demographics' | 'studyIds';
  subjectId: string;
  currentValues: Record<string, string | undefined>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const keys = SECTION_FIELDS[section];
  // Only seed values that aren't Safe-Harbor masks. If we can't read the
  // plaintext, the field starts blank so the caller doesn't accidentally
  // save the mask string back into the store.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      keys.map((k) => [
        k,
        typeof currentValues[k] === 'string' ? (currentValues[k] as string) : '',
      ]),
    ),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (section === 'contact') {
        // Contact routes through the existing recordContactInfo endpoint,
        // which rejects anything that isn't E.164. Normalise US-style entry
        // ("(415) 555-1234") up front so the coordinator isn't forced to
        // hand-write a country code. Falls through unchanged when we can't
        // resolve it, so the backend still owns the final word.
        const rawPhone = values.phone_e164 ?? '';
        return subjectsWizard.recordContactInfo(subjectId, {
          email_personal: values.email_personal ?? '',
          phone_e164: rawPhone ? (normalisePhone(rawPhone) ?? rawPhone) : '',
        });
      }
      if (section === 'demographics') {
        return subjectsWizard.recordDemographics(subjectId, {
          sex: values.demographics_sex || null,
          gender: values.demographics_gender || null,
        });
      }
      if (section === 'studyIds') {
        // Rides /profile like identity and address, but the server splits it
        // out of the encrypted bundle and writes ctms_subjects.ivrf_number.
        // '' clears it, matching how the PHI fields blank.
        return subjectsWizard.updateProfile(subjectId, {
          ivrf_number: values.ivrf_number ?? '',
        });
      }
      // Identity / Address / Highlights go through the new /profile endpoint.
      const payload: Record<string, string> = {};
      for (const k of keys) payload[k] = values[k] ?? '';
      return subjectsWizard.updateProfile(subjectId, payload);
    },
    onSuccess: () => {
      toast.success('Profile updated');
      qc.invalidateQueries({ queryKey: ['subject', subjectId] });
      onClose();
    },
    onError: (e: unknown) => {
      setError(errorText(e, 'Failed to save.'));
    },
  });

  const title =
    section === 'identity'
      ? 'Edit identity'
      : section === 'contact'
        ? 'Edit contact'
        : section === 'address'
          ? 'Edit address'
          : section === 'demographics'
            ? 'Edit demographics'
            : section === 'studyIds'
              ? 'Edit study identifiers'
              : 'Edit highlights';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-lg">
        <header className="flex items-center border-b border-surface-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded p-1 text-ink-muted hover:bg-surface-hover"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 p-4">
          {error && (
            <div className="flex items-start gap-2 rounded bg-danger-50 p-3 text-sm text-danger-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {keys.map((k) => (
            <label key={k} className="block text-sm">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-muted">
                {LABELS[k] ?? k}
              </span>
              {k === 'highlights' ? (
                <textarea
                  rows={4}
                  value={values[k] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                  className="w-full rounded border border-surface-border bg-white px-2 py-1.5 text-sm text-ink-primary focus:border-accent-500 focus:outline-none"
                  placeholder="Wheelchair user, latex allergy, prefers morning visits…"
                />
              ) : k === 'demographics_sex' || k === 'demographics_gender' ? (
                <select
                  value={values[k] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                  className="w-full rounded border border-surface-border bg-white px-2 py-1.5 text-sm text-ink-primary focus:border-accent-500 focus:outline-none"
                >
                  <option value="">— select —</option>
                  {(k === 'demographics_sex' ? SEX_OPTIONS : GENDER_OPTIONS).map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={k === 'email_personal' ? 'email' : k === 'date_of_birth' ? 'text' : 'text'}
                  value={values[k] ?? ''}
                  maxLength={k === 'ivrf_number' ? 60 : undefined}
                  onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                  placeholder={
                    k === 'date_of_birth'
                      ? 'YYYY-MM-DD (or YYYY / YYYY-MM)'
                      : k === 'phone_e164'
                        ? '+14155551234'
                        : k === 'ivrf_number'
                          ? 'From the IVRS / IWRS (optional)'
                          : undefined
                  }
                  className="w-full rounded border border-surface-border bg-white px-2 py-1.5 text-sm text-ink-primary focus:border-accent-500 focus:outline-none"
                />
              )}
            </label>
          ))}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-surface-border bg-surface-muted px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border bg-white px-3 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-hover"
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            disabled={save.isPending}
            className="inline-flex items-center gap-1 rounded bg-accent-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}
